import { downloadReportFile, getContents, getContentsByTopics } from "@/services/api";
import { getDownloadHistory, getReportHistory } from "@/services/api/User";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useDebounce } from "use-debounce";
import useAuth from "./auth";
import useGetContentTypeUuid from "./useGetContentUuid";
import slugify from "@/utils/slugify";

export function useDownloadReport({ report, onSuccessCallback }) {
  const { user } = useAuth();
  const [isDownloading, setDownloading] = useState(false);

  const triggerDownload = (url, filename = 'report.pdf') => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadReport = async () => {
    if (!user) {
      toast.error("Please login to download report!");
      return false;
    }

    setDownloading(true);

    try {
      const response = await downloadReportFile(report.uuid);

      // Trigger direct download using the download_url from API response
      if (response.data?.success && response.data?.download_url) {
        triggerDownload(response.data.download_url, `${report.title}.pdf`);
        toast.success("Your download has started!", {
          duration: 5000,
        });
      } else {
        toast.error("Download failed. Please try again!");
        return false;
      }

      if (onSuccessCallback) {
        onSuccessCallback();
      }

      return true;
    } catch (error) {
      toast.error(
        error?.response?.data?.limit_reached
          ? error.response.data.message
          : "Failed to download report. Please try again!"
      );
      return false;
    } finally {
      setDownloading(false);
    }
  };

  return { isDownloading, downloadReport };
}

export function useReportReadHistory() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");

  const fetchReportReadHistory = async () => {
    try {
      const response = await getReportHistory();
      setItems(response.data.data);
    } catch (error) {
      console.log("Error", error);
      toast.error("Failed to fetch download history. Please try again!");
    }
  };

  useEffect(() => {
    fetchReportReadHistory();
  }, [user]);

  return { items, isLoading, fetchReportReadHistory, filter, setFilter };
}

export function useGetContentList({
  type,
  onErrorCallback,
  initialData,
  is_random,
  need_pagination,
  perPage,
}) {
  const contentType = useGetContentTypeUuid(type);
  const [search, setSearch] = useState("");
  const [topic, setTopic] = useState();
  const [contents, setContents] = useState(initialData || []);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [searchQuery] = useDebounce(search, 500);
  const [sortInput, setSortInput] = useState('newest')
  const [tags, setTags] = useState("");
  const [categoryId, setCategoryId] = useState("");
  // Bumped on every fetch, so a response that lands after a newer request
  // (e.g. page 2 of the previous search) is dropped instead of mixed in.
  const requestIdRef = useRef(0);

  const fetchContents = async function (page) {
    if (!contentType) null;
    const requestId = ++requestIdRef.current;

    try {
      setLoading(true);

      let filter = {
        // is_random,
        need_pagination
      };
      if (searchQuery) {
        filter["search"] = searchQuery;
      }

      if (tags) {
        filter["tags"] = tags;
      }

      if (categoryId) {
        filter["category_id"] = categoryId;
      }

      if (topic) {
        filter["topicUuid"] = topic;
      }

      if (sortInput) {
        filter['sort'] = sortInput
      }

      filter["contentTypeUuid"] = contentType.uuid;
      filter["page"] = page;
      if (perPage) {
        filter["per_page"] = perPage;
      }

      const response = await getContents(filter);
      if (requestId !== requestIdRef.current) return;
      const result = response.data;
      const activePage = result ? result.meta.current_page : 1;
      setTotal(result.meta.total);
      setLastPage(result.meta.last_page);

      if (page === 1) {
        setContents(result.data || []);
      } else {
        // Append onto the latest list, not the one this closure captured.
        setContents((prev) => {
          const ids = new Set(prev.map((item) => item.uuid));
          return [...prev, ...result.data.filter((v) => !ids.has(v.uuid))];
        });
      }

      if (activePage > 0) {
        setCurrentPage(activePage);
      }
    } catch (error) {
      if (requestId === requestIdRef.current && onErrorCallback) {
        onErrorCallback(error);
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  // Keyed on the uuid: _app re-fetches content types on full route changes,
  // and a new object for the same type would reset the list to page 1.
  useEffect(() => {
    fetchContents(1);
  }, [searchQuery, topic, contentType?.uuid, sortInput, tags, categoryId]);

  return {
    search,
    setSearch,
    contents,
    currentPage,
    lastPage,
    total,
    isLoading,
    topic,
    setTopic,
    fetchContents,
    sortInput,
    setSortInput,
    tags,
    setTags,
    categoryId,
    setCategoryId
  };
}

export function useGetContentByTopicDetail({
  type,
  onErrorCallback,
  initialData,
  is_random,
  topicName,
  need_pagination
}) {
  const contentType = useGetContentTypeUuid(type);
  const [search, setSearch] = useState("");
  const [topic, setTopic] = useState();
  const [contents, setContents] = useState(initialData || []);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [searchQuery] = useDebounce(search, 500);
  const [sortInput, setSortInput] = useState('newest')

  const fetchContents = async function (page) {
    if (!contentType) null;

    try {
      setLoading(true);

      let filter = {
        is_random,
        need_pagination
      };
      if (searchQuery) {
        filter["search"] = searchQuery;
      }
      if (topic) {
        filter["topicUuid"] = topic;
      }

      if (topicName) {
        console.log(topicName)
        filter['topicName'] = topicName
      }

      filter["contentTypeUuid"] = contentType.uuid;
      filter["page"] = page;

      const response = await getContentsByTopics(filter);
      const result = response.data;
      const activePage = result ? result.meta?.current_page : 1;
      setTotal(result.meta?.total);
      setLastPage(result.meta?.last_page);

      const firstData = result?.data || result || [];
      if (page === 1) {
        setContents(firstData);
    } else {
        const ids = contents.map(item => item.uuid);
        const filteredData = firstData.filter(v => !ids.includes(v.uuid));
        setContents([...contents, ...filteredData]);
    }

      if (activePage > 0) {
        setCurrentPage(activePage);
      }
    } catch (error) {
      if (onErrorCallback) {
        onErrorCallback(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContents(1);
  }, [searchQuery, topic, contentType, sortInput]);

  return {
    search,
    setSearch,
    contents,
    currentPage,
    lastPage,
    total,
    isLoading,
    topic,
    setTopic,
    fetchContents,
    sortInput,
    setSortInput
  };
}


export function useGetContentByTopicList({
  type,
  onErrorCallback,
  initialData,
  is_random,
  need_pagination
}) {
  const contentType = useGetContentTypeUuid(type);
  const [search, setSearch] = useState("");
  const [topic, setTopic] = useState();
  const [contents, setContents] = useState(initialData || []);
  const [isLoading, setLoading] = useState(false);
  const [searchQuery] = useDebounce(search, 500);
  const [sortInput, setSortInput] = useState('newest')

  const fetchContents = async function () {
    if (!contentType) return null;

    try {
      setLoading(true);

      let filter = {
        is_random,
        need_pagination
      };
      if (searchQuery) {
        filter["search"] = searchQuery;
      }
      if (topic) {
        filter["topicUuid"] = topic;
      }
      filter["contentTypeUuid"] = contentType.uuid;

      if (sortInput) {
        filter['sort'] = sortInput
      }

      const response = await getContentsByTopics(filter);

      // Extract the required data for each topic and filter out empty data arrays
      console.log(response.data)
      const updatedTopics = Object.keys(response.data)
        .map(topicName => ({
          name: topicName,
          data: response.data[topicName].data.data || response.data[topicName].data,
          slug: response.data[topicName].slug,
        }))
        .filter(topic => topic.data.length > 0);

      // Set the contents with updated topics
      setContents(updatedTopics);
    } catch (error) {
      if (onErrorCallback) {
        onErrorCallback(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContents();
  }, [searchQuery, topic, contentType, sortInput]);

  return {
    search,
    setSearch,
    contents,
    isLoading,
    topic,
    setTopic,
    fetchContents,
    sortInput,
    setSortInput
  };
}
